import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Divider,
  mergeClasses,
  Spinner,
  Checkbox,
} from "@fluentui/react-components";
import React, { useState } from "react";
import { Grid } from "../common/grid/Grid";
import { GridItem } from "../common/grid/GridItem";
import { t } from "../../i18n/i18n";
import { CtaButton } from "../common/input/CtaButton";
import { useFormContext } from "react-hook-form";
import { AdditionalModule, BaseModuleItem, ProcessItem, OrderFormData, Product } from "../../interfaces/OrderFormData";
import { calculateMonthlyCost } from "../../helper/orderHelper";
import { formatPrice } from "../../helper/orderHelper";

const useStyles = makeStyles({
  grid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",

    ...shorthands.gap(tokens.spacingHorizontalS, tokens.spacingVerticalS),
    "@media screen and (min-width: 600px)": {
      ...shorthands.gap(tokens.spacingHorizontalM, 0),
    },
    "@media screen and (min-width: 800px)": {
      ...shorthands.gap(
        `calc(${tokens.spacingHorizontalXS} * 2)`,
        `calc(${tokens.spacingVerticalS} * 2)`
      ),
    },
  },
  flexCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  button: {
    ...shorthands.margin(tokens.spacingVerticalXL, "auto"),
    width: "75%",
    height: "50px",
  },
  marginBottomM: {
    ...shorthands.margin(0, 0, tokens.spacingVerticalM, 0),
  },
  marginBottomXS: {
    ...shorthands.margin(0, 0, tokens.spacingVerticalXS, 0),
  },
  hideInPhone: {
    "@media screen and (max-width: 1300px)": {
      display: "none",
    },
  },
  showInPhone: {
    display: "none",
    "@media screen and (max-width: 1300px)": {
      display: "block",
    },
  },
  phoneRows: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "start",
    ...shorthands.margin(tokens.spacingVerticalS, 0),
  },
  phoneCategoryHeader: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.margin(
      tokens.spacingHorizontalM,
      0,
      tokens.spacingVerticalXL,
      0
    ),
  },
  spaceBetween: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonPhone: {
    width: "100%",
  },
});

type BasketProps = {
  onNextClick?: () => void;
  onSubmitClick?: (data: OrderFormData) => void;
  step: number;
  loading?: boolean;
  showPrices?: boolean;
};

export const Basket = ({
  onNextClick,
  onSubmitClick,
  step,
  loading,
  showPrices = false,
}: BasketProps) => {
  const classes = useStyles();
  const { watch, handleSubmit } = useFormContext<OrderFormData>();
  const [checked, setChecked] = useState<boolean>(false);
  const products = watch("products") ?? [];
  const additionalModules = watch("additionalModules") ?? [];
  const baseModules = watch("baseModules") ?? [];
  const processes = watch("processes") ?? [];

  const basketPhone = () => {
    return (
      <>
        <section className={mergeClasses(classes.flexCol, classes.showInPhone)}>
          <Text size={600} weight="bold">
            {t("Basket")}
          </Text>
          <BasketProductsPhone products={products} showPrices={showPrices} />

          <BasketBaseModulesPhone baseModules={baseModules} showPrices={showPrices} />

          <BasketDemandAreasPhone processes={processes} showPrices={showPrices} />

          <BasketAdditionalModulesPhone additionalModules={additionalModules} showPrices={showPrices} />

          {/* Monthly cost */}
          {showPrices && (
            <div className={classes.spaceBetween}>
              <Text size={400} weight="semibold">
                {t("TotalMonthlyCost")}
              </Text>
              <Text size={600} weight="bold">
                {calculateMonthlyCost(products, additionalModules, baseModules, processes)}
                <Text size={300}> {t("CurrencyShortForm")}</Text>
              </Text>
            </div>
          )}
          <CtaButton
            buttonText={
              loading ? (
                <Spinner appearance="inverted" />
              ) : step === 3 ? (
                t("ConfirmOrder")
              ) : (
                t("Next")
              )
            }
            disabled={loading || (step === 3 && !checked)}
            type="button"
            className={mergeClasses(classes.button, classes.buttonPhone)}
            onClick={step === 3 ? handleSubmit(onSubmitClick!) : onNextClick}
          />
          <Checkbox
            size="large"
            style={{ fontSize: tokens.fontSizeBase400 }}
            label={
              t("PolicyAcceptance")
            }
            required
            checked={checked ?? false}
            onChange={(e, data) => setChecked(data.checked === true)}
          />
        </section>
      </>
    );
  };

  return (
    <>
      {basketPhone()}
      <section className={mergeClasses(classes.flexCol, classes.hideInPhone)}>
        <Grid className={classes.grid}>
          <GridItem size="12/12" className={classes.marginBottomM}>
            <Text size={600} weight="bold">
              {t("Basket")}
            </Text>
          </GridItem>

          <GridItem
            size={showPrices ? "5/12" : "9/12"}
            className={classes.marginBottomXS}
          >
            <Text size={400}>{t("Product")}</Text>
          </GridItem>
          <GridItem size={"3/12"} className={classes.marginBottomXS}>
            <Text size={400}>{t("Quantity")}</Text>
          </GridItem>
          <>
            {showPrices && (
              <GridItem size="4/12" className={classes.marginBottomXS}>
                <Text size={400}>{t("MonthlyCost")}</Text>
              </GridItem>
            )}
          </>
          <BasketProducts products={products} showPrices={showPrices} />

          <BasketBaseModules baseModules={baseModules} showPrices={showPrices} />

          <BasketDemandAreas processes={processes} showPrices={showPrices} />

          <BasketAdditionalModules additionalModules={additionalModules} showPrices={showPrices} />

          <>
            {showPrices && (
              <>
                <GridItem size="8/12">
                  <Text size={500} weight="semibold">
                    {t("TotalMonthlyCost")}
                  </Text>
                </GridItem>
                <GridItem size="4/12">
                  <Text size={800} weight="bold">
                    {calculateMonthlyCost(products, additionalModules, baseModules, processes)}
                    <Text size={500}> {t("CurrencyShortForm")}</Text>
                  </Text>
                </GridItem>
              </>
            )}
          </>
        </Grid>
        <CtaButton
          buttonText={
            loading ? (
              <Spinner appearance="inverted" />
            ) : step === 3 ? (
              t("ConfirmOrder")
            ) : (
              t("Next")
            )
          }
          disabled={loading || (step === 3 && !checked)}
          type="button"
          className={classes.button}
          onClick={step === 3 ? handleSubmit(onSubmitClick!) : onNextClick}
        />
        <Checkbox
          size="large"
          style={{ fontSize: tokens.fontSizeBase400 }}
          label={
            t("PolicyAcceptance")
          }
          required
          checked={checked ?? false}
          onChange={(e, data) => setChecked(data.checked === true)}
        />
      </section>
    </>
  );
};

type BasketProductsProps = {
  products: Product[];
  showPrices: boolean;
};

export const BasketProducts = ({
  products,
  showPrices,
}: BasketProductsProps) => {
  return (
    <>
      {products.map((product) => (
        <>
          <GridItem size={showPrices ? "5/12" : "9/12"}>
            <Text size={400}>{product.name}</Text>
          </GridItem>
          <GridItem size="3/12">
            <Text size={400}>{product.quantity}</Text>
          </GridItem>
          {showPrices && (
            <GridItem size="4/12">
              <Text size={400}>
                {product?.monthlyCost === 0
                  ? t("Included")
                  : `${formatPrice(product.monthlyCost ?? 0)} kr`}
              </Text>
            </GridItem>
          )}
        </>
      ))}
      <GridItem size="12/12">
        <Divider appearance="strong" />
      </GridItem>
    </>
  );
};
export const BasketProductsPhone = ({
  products,
  showPrices,
}: BasketProductsProps) => {
  const classes = useStyles();
  return (
    <section className={classes.phoneCategoryHeader}>
      <div className={classes.spaceBetween}>
        <Text size={400}>{t("Product")}</Text>
        {showPrices && <Text size={400}>{t("MonthlyCost")}</Text>}
      </div>

      {products.map((product) => (
        <div className={classes.phoneRows}>
          <Text size={400}>{product.quantity}st</Text>
          <Text size={400}>{product.name}</Text>
          {showPrices && (
            <Text size={400}>
              {product?.monthlyCost === 0
                ? t("Included")
                : showPrices && `${formatPrice(product.monthlyCost ?? 0)} kr`}
            </Text>
          )}
        </div>
      ))}
      <GridItem size="12/12">
        <Divider appearance="strong" />
      </GridItem>
    </section>
  );
};

type BasketBaseModulesProps = {
  baseModules: BaseModuleItem[];
  showPrices: boolean;
};

export const BasketBaseModules = ({
  baseModules,
  showPrices,
}: BasketBaseModulesProps) => {
  const classes = useStyles();

  if (baseModules.length === 0) return null;

  return (
    <>
      <GridItem size="12/12" className={classes.marginBottomXS}>
        <Text size={400} weight="semibold">Basmoduler</Text>
      </GridItem>
      {baseModules.map((bm, idx) => (
        <React.Fragment key={idx}>
          <GridItem size="8/12">
            <Text size={400}>{bm.name}</Text>
          </GridItem>
          <GridItem size="4/12">
            <Text size={400}>
              {bm.monthlyCost === 0
                ? t("Included")
                : showPrices
                ? `${formatPrice(bm.monthlyCost)} ${t("CurrencyShortForm")}`
                : null}
            </Text>
          </GridItem>
        </React.Fragment>
      ))}
      <GridItem size="12/12">
        <Divider appearance="strong" />
      </GridItem>
    </>
  );
};

export const BasketBaseModulesPhone = ({
  baseModules,
  showPrices,
}: BasketBaseModulesProps) => {
  const classes = useStyles();

  if (baseModules.length === 0) return null;

  return (
    <section className={classes.phoneCategoryHeader}>
      <Text size={400} weight="semibold">Basmoduler</Text>
      {baseModules.map((bm, idx) => (
        <div className={classes.phoneRows} key={idx}>
          <Text size={400}>{bm.name}</Text>
          <Text size={400}>
            {bm.monthlyCost === 0
              ? t("Included")
              : showPrices
              ? `${formatPrice(bm.monthlyCost)} ${t("CurrencyShortForm")}`
              : null}
          </Text>
        </div>
      ))}
      <Divider appearance="strong" />
    </section>
  );
};

type BasketDemandAreasProps = {
  processes: ProcessItem[];
  showPrices: boolean;
};

export const BasketDemandAreas = ({
  processes,
  showPrices,
}: BasketDemandAreasProps) => {
  const classes = useStyles();

  if (processes.length === 0) return null;

  return (
    <>
      <GridItem size="12/12" className={classes.marginBottomXS}>
        <Text size={400} weight="semibold">Processer</Text>
      </GridItem>
      {processes.map((k, idx) => (
        <React.Fragment key={idx}>
          <GridItem size="8/12">
            <Text size={400}>{k.name}</Text>
          </GridItem>
          <GridItem size="4/12">
            <Text size={400}>
              {k.monthlyCost === 0
                ? t("Included")
                : showPrices
                ? `${formatPrice(k.monthlyCost)} ${t("CurrencyShortForm")}`
                : null}
            </Text>
          </GridItem>
        </React.Fragment>
      ))}
      <GridItem size="12/12">
        <Divider appearance="strong" />
      </GridItem>
    </>
  );
};

export const BasketDemandAreasPhone = ({
  processes,
  showPrices,
}: BasketDemandAreasProps) => {
  const classes = useStyles();

  if (processes.length === 0) return null;

  return (
    <section className={classes.phoneCategoryHeader}>
      <Text size={400} weight="semibold">Processer</Text>
      {processes.map((k, idx) => (
        <div className={classes.phoneRows} key={idx}>
          <Text size={400}>{k.name}</Text>
          <Text size={400}>
            {k.monthlyCost === 0
              ? t("Included")
              : showPrices
              ? `${formatPrice(k.monthlyCost)} ${t("CurrencyShortForm")}`
              : null}
          </Text>
        </div>
      ))}
      <Divider appearance="strong" />
    </section>
  );
};

type BasketAdditionalModulesProps = {
  additionalModules: AdditionalModule[];
  showPrices: boolean;
};

export const BasketAdditionalModules = ({ additionalModules, showPrices }: BasketAdditionalModulesProps) => {
  const classes = useStyles();

  return (
    <>
      {additionalModules.length > 0 && (
        <>
          <GridItem size="12/12" className={classes.marginBottomXS}>
            <Text size={400} weight="semibold">{t("Addons")}</Text>
          </GridItem>
          {additionalModules.map((addon, idx) => {
            return (
              <React.Fragment key={idx}>
                <GridItem size="8/12">
                  <Text size={400}>{addon.name}</Text>
                </GridItem>

                <GridItem size="4/12">
                  <Text size={400}>
                    {addon.monthlyCost === 0
                      ? t("Included")
                      : showPrices ? `${formatPrice(addon.monthlyCost)} ${t(
                          "CurrencyShortForm"
                        )}` : null}
                  </Text>
                </GridItem>
              </React.Fragment>
            );
          })}
          <GridItem size="12/12">
            <Divider appearance="strong" />
          </GridItem>
        </>
      )}
    </>
  );
};

export const BasketAdditionalModulesPhone = ({
  additionalModules,
  showPrices,
}: BasketAdditionalModulesProps) => {
  const classes = useStyles();

  return (
    <>
      {additionalModules.length > 0 && (
        <section className={classes.phoneCategoryHeader}>
          <Text size={400} weight="semibold">{t("Addons")}</Text>
          {additionalModules.map((addon, idx) => (
            <div className={classes.phoneRows} key={idx}>
              <Text size={400}>{addon.name}</Text>

              <Text size={400}>
                {addon.monthlyCost === 0
                  ? t("Included")
                  : showPrices ? `${formatPrice(addon.monthlyCost)} ${t(
                      "CurrencyShortForm"
                    )}` : null}
              </Text>
            </div>
          ))}
          <Divider appearance="strong" />
        </section>
      )}
    </>
  );
};
